"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Award,
  Brain,
  Briefcase,
  FileUp,
  Lightbulb,
  Mail,
  Send,
  ShieldCheck,
  User,
  X,
} from "lucide-react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import BackButton from "../shared/backButton";
import {
  isProfileInRoleShortlist,
  readHiringShortlists,
  subscribeHiringShortlists,
  toggleProfileInRoleShortlist,
} from "../shared/utils/hiringShortlistState";
import MessageManager from "../layout/Message";
import "./ExternalCandidateProfilePage.css";

import {
  viewExternalProfileApi,
  contactPartnerApi,
} from "../../services/hiringService";

const readStoredExternalProfileContext = () => {
  try {
    const storedContext = sessionStorage.getItem("mmhExternalCandidateProfile");

    return storedContext ? JSON.parse(storedContext) : null;
  } catch (error) {
    console.error("Unable to restore external profile context:", error);

    return null;
  }
};

const fallbackProfiles = {
  "EXT-1042": {
    id: "EXT-1042",
    candidateName: "Tanya Bhatia",
    candidateEmail: "tanya.bhatia@example.com",
    canViewCandidateDetails: true,
    partner: "Apex Talent Partners",
    availability: "Available",
    completedQuestionsCount: 45,
    profileStatus: "Questions, Skill Profile, Resume completed",
    mayamayaAssessment: {
      quizCompleted: true,
      skillAssessmentCompleted: true,
      resumeUploadCompleted: true,
    },
    roleMatches: [
      { title: "Backend Developer", department: "Engineering", match: 86 },
      {
        title: "Business Intelligence Analyst",
        department: "Data Science",
        match: 81,
      },
      {
        title: "Documentation Specialist",
        department: "Knowledge Management",
        match: 76,
      },
    ],
    skills: ["API Design", "Data Modeling", "Problem Solving", "Documentation"],
  },
};

const getInitials = (value = "") =>
  value
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "EX";

const isProtectedExternalProfileName = (name) =>
  String(name || "")
    .trim()
    .toUpperCase()
    .startsWith("EXT");

const clampPercentage = (value) => {
  const numericValue = Number(value);

  if (!Number.isFinite(numericValue)) {
    return 0;
  }

  return Math.max(0, Math.min(100, numericValue));
};

const normalizeSoftskillScores = (softskillScores) => {
  if (!softskillScores || typeof softskillScores !== "object") {
    return [];
  }

  return Object.entries(softskillScores)
    .map(([name, value]) => {
      const numericValue = Number(value);
      const scoreValue = Number.isFinite(numericValue)
        ? numericValue <= 10
          ? numericValue * 10
          : numericValue
        : 0;

      return {
        name,
        score: clampPercentage(scoreValue),
        rawScore: Number.isFinite(numericValue) ? numericValue : value,
      };
    })
    .sort(
      (left, right) =>
        right.score - left.score || left.name.localeCompare(right.name),
    );
};

const normalizeTopMatches = (top3Matches) => {
  if (!Array.isArray(top3Matches)) {
    return [];
  }

  return top3Matches.filter(Boolean).map((match, index) => ({
    id: String(match?.jobId || match?._id || `top-match-${index + 1}`),
    jobId: match?.jobId || "",
    title: match?.roleName || match?.title || "Untitled role",
    department: match?.department || "-",
    level: match?.level || "-",
    match: clampPercentage(
      match?.totalMatchPercentage ?? match?.match ?? match?.score,
    ),
  }));
};

const deriveProfileStatus = (data) => {
  const statusItems = [
    { label: "Questions", done: Boolean(data?.quizStatus) },
    { label: "Skill Profile", done: Boolean(data?.careerProfileStatus) },
    { label: "Resume", done: Boolean(data?.resumeStatus) },
  ];

  if (statusItems.every((item) => item.done)) {
    return "Questions, Skill Profile, Resume completed";
  }

  if (statusItems.every((item) => !item.done)) {
    return "Profile incomplete";
  }

  return statusItems
    .map((item) => `${item.label} ${item.done ? "completed" : "pending"}`)
    .join(", ");
};

const normalizeExternalProfileData = (data, fallbackProfile = {}) => {
  const source = data && typeof data === "object" ? data : {};
  const fallback =
    fallbackProfile && typeof fallbackProfile === "object"
      ? fallbackProfile
      : {};

  const rawUserId = String(
    source.userId ||
      source.externalCode ||
      source.id ||
      fallback.userId ||
      fallback.externalCode ||
      fallback.id ||
      "",
  ).trim();

  const externalCode = String(
    source.externalCode ||
      rawUserId ||
      fallback.externalCode ||
      fallback.userId ||
      fallback.id ||
      "",
  ).trim();

  const displayId =
    externalCode ||
    rawUserId ||
    fallback.id ||
    fallback.userId ||
    source._id ||
    "External Profile";

  const skillScores = normalizeSoftskillScores(
    source.softskillScores || fallback.softskillScores,
  );
  const fallbackSkillScores = Array.isArray(fallback.skillScores)
    ? fallback.skillScores
    : Array.isArray(fallback.skills)
      ? fallback.skills.map((skill) => ({ name: skill }))
      : [];
  const normalizedSkillScores =
    skillScores.length > 0 ? skillScores : fallbackSkillScores;
  const profileIdentifiers = new Set(
    [
      source.externalCode,
      source.userId,
      source.id,
      source._id,
      fallback.externalCode,
      fallback.userId,
      fallback.id,
    ]
      .map((value) =>
        String(value ?? "")
          .trim()
          .toLowerCase(),
      )
      .filter(Boolean),
  );

  const displayNameCandidates = [
    source.candidateName,
    source.candidate_name,
    source.fullName,
    source.full_name,
    source.displayName,
    source.profileName,
    source.profile?.candidateName,
    source.profile?.name,
    source.profile?.fullName,
    source.user?.fullName,
    source.user?.name,
    source.userDetails?.fullName,
    source.userDetails?.name,
    source.candidate?.fullName,
    source.candidate?.name,
    fallback.candidateName,
    fallback.fullName,
    fallback.displayName,
  ]
    .map((value) => String(value ?? "").trim())
    .filter(Boolean);

  const descriptiveProfileName =
    displayNameCandidates.find(
      (value) => !profileIdentifiers.has(value.toLowerCase()),
    ) || "";

  const apiProfileName = String(source.name ?? fallback.name ?? "").trim();

  const profileName =
    descriptiveProfileName || apiProfileName || displayId || "";

  return {
    ...fallback,
    ...source,
    _id: source._id || fallback._id || "",
    mongoId: source._id || fallback._id || "",
    id: displayId,
    profileId: displayId,
    externalCode: externalCode || displayId,
    userId: rawUserId || externalCode || displayId,
    name: profileName,
    candidateName: profileName,
    email: source.email || fallback.candidateEmail || fallback.email || "",
    candidateEmail:
      source.email || fallback.candidateEmail || fallback.email || "",
    partner:
      source.partnerOrganizationName ||
      source.partner ||
      fallback.partner ||
      "Partner organization",
    partnerId:
      source.partnerId ||
      source.partnerTenantId ||
      fallback.partnerId ||
      fallback.partnerTenantId ||
      "",
    partnerTenantId:
      source.partnerTenantId ||
      source.partnerId ||
      fallback.partnerTenantId ||
      fallback.partnerId ||
      "",
    availability: source.availability || fallback.availability || "Unknown",
    completedQuestionsCount:
      Number(
        source.completedQuestionCount ?? fallback.completedQuestionsCount ?? 0,
      ) || 0,
    profileStatus:
      source.profileStatus ||
      fallback.profileStatus ||
      deriveProfileStatus(source),
    canViewCandidateDetails:
      typeof fallback.canViewCandidateDetails === "boolean"
        ? fallback.canViewCandidateDetails
        : Boolean(source.canViewCandidateDetails),
    mayamayaAssessment: {
      quizCompleted: Boolean(
        source.quizStatus ?? fallback?.mayamayaAssessment?.quizCompleted,
      ),
      skillAssessmentCompleted: Boolean(
        source.careerProfileStatus ??
        fallback?.mayamayaAssessment?.skillAssessmentCompleted,
      ),
      resumeUploadCompleted: Boolean(
        source.resumeStatus ??
        fallback?.mayamayaAssessment?.resumeUploadCompleted,
      ),
    },
    roleMatches:
      normalizeTopMatches(source.top3Matches).length > 0
        ? normalizeTopMatches(source.top3Matches)
        : Array.isArray(fallback.roleMatches)
          ? fallback.roleMatches
          : [],
    skillScores: normalizedSkillScores,
    skills:
      normalizedSkillScores.length > 0
        ? normalizedSkillScores.map((skill) => skill.name)
        : Array.isArray(fallback.skills)
          ? fallback.skills
          : [],
    performanceMetrics: Array.isArray(source.performanceMetrics)
      ? source.performanceMetrics
      : Array.isArray(fallback.performanceMetrics)
        ? fallback.performanceMetrics
        : [],
    rawProfile: source,
  };
};

const extractExternalProfileData = (response) =>
  response?.data?.data ??
  response?.data ??
  response?.profile ??
  response ??
  null;

const ExternalCandidateProfilePage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState("overview");
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [partnerMessage, setPartnerMessage] = useState("");
  const [isSendingPartnerMessage, setIsSendingPartnerMessage] =
    useState(false);

  const profileContext = useMemo(
    () => location.state || readStoredExternalProfileContext(),
    [location.state],
  );

  const selectedRole = profileContext?.role || null;
  const sourceProfile = profileContext?.profile || fallbackProfiles[id] || null;
  const defaultExternalProfile = useMemo(
    () =>
      sourceProfile || {
        id: id || "External Profile",
        partner: "Partner organization",
        availability: "Unknown",
        canViewCandidateDetails: false,
        profileStatus: "Profile shared by partner",
        mayamayaAssessment: {},
        roleMatches: [],
        skills: [],
      },
    [id, sourceProfile],
  );

  const [shortlists, setShortlists] = useState(readHiringShortlists);
  const [externalProfileApiData, setExternalProfileApiData] = useState(null);

  useEffect(() => {
    return subscribeHiringShortlists(setShortlists);
  }, []);

  const externalProfile = useMemo(() => {
    const normalizedProfile = normalizeExternalProfileData(
      externalProfileApiData || defaultExternalProfile,
      sourceProfile || {},
    );

    if (externalProfileApiData) {
      normalizedProfile.profileStatus =
        externalProfileApiData.profileStatus ||
        deriveProfileStatus(externalProfileApiData);
    }

    return normalizedProfile;
  }, [defaultExternalProfile, externalProfileApiData, sourceProfile]);

  const externalProfileRequestUserId = useMemo(() => {
    const candidateProfile = profileContext?.profile || {};

    return String(
      candidateProfile.userId ||
        candidateProfile.externalCode ||
        candidateProfile.id ||
        id ||
        "",
    ).trim();
  }, [id, profileContext]);

  const externalProfileRequestJobId = useMemo(() => {
    const roleContext = selectedRole || {};

    return String(
      roleContext.jobId || profileContext?.jobId || roleContext.id || "",
    ).trim();
  }, [profileContext, selectedRole]);

  const isSaved =
    Boolean(selectedRole?.id) &&
    isProfileInRoleShortlist(shortlists, selectedRole.id, externalProfile.id);

  const displayName = externalProfile.name || "";
  const isIdentityProtected = isProtectedExternalProfileName(
    externalProfile.rawProfile?.name || displayName,
  );
  const canShowIdentity = !isIdentityProtected;
  const sharedEmail =
    externalProfile.candidateEmail || externalProfile.email || "";
  const displayEmail = canShowIdentity
    ? sharedEmail || "Not shared"
    : "Hidden by partner settings";

  const assessment = externalProfile.mayamayaAssessment || {};
  const roleMatches =
    (Array.isArray(externalProfile.roleMatches) &&
    externalProfile.roleMatches.length > 0
      ? externalProfile.roleMatches
      : null) ||
    [
      {
        title: externalProfile.role,
        department: externalProfile.department,
        match: 86,
      },
      {
        title: "Business Intelligence Analyst",
        department: "Data Science",
        match: 81,
      },
      {
        title: "Documentation Specialist",
        department: "Knowledge Management",
        match: 76,
      },
    ].filter((item) => item.title);
  const skills = externalProfile.skills || [];
  const skillPerformanceItems = Array.isArray(externalProfile.skillScores)
    ? externalProfile.skillScores.length > 0
      ? externalProfile.skillScores
      : skills.map((skill) => ({ name: skill }))
    : skills.map((skill) => ({ name: skill }));
  const performanceMetrics = Array.isArray(externalProfile.performanceMetrics)
    ? externalProfile.performanceMetrics
    : Array.isArray(externalProfile.performance)
      ? externalProfile.performance
      : [];

  const tabs = [
    { id: "overview", label: "Overview", icon: User },
    { id: "skills", label: "Skills & Performance", icon: Brain },
    { id: "matches", label: "Role Matches", icon: Briefcase },
  ];

  const openContactPartner = () => {
    setPartnerMessage(
      "Hi, we're interested in this profile and would appreciate any additional information you can share. Thank you!",
    );
    setContactModalOpen(true);
  };

  const sendPartnerMessage = async () => {
    const message = partnerMessage.trim();
    const partnerId = String(
      externalProfile.partnerId ||
        externalProfile.partnerTenantId ||
        sourceProfile?.partnerId ||
        sourceProfile?.partnerTenantId ||
        profileContext?.partnerId ||
        selectedRole?.partnerId ||
        selectedRole?.partnerTenantId ||
        "",
    ).trim();
    const jobId = String(externalProfileRequestJobId || selectedRole?.jobId || "").trim();
    const userId = String(
      externalProfile.userId ||
        externalProfile.externalCode ||
        externalProfileRequestUserId ||
        "",
    ).trim();

    if (!message) {
      MessageManager.showToast("error", "Please enter a message.");
      return;
    }

    if (!partnerId || !jobId || !userId) {
      MessageManager.showToast(
        "error",
        "Unable to send message. Partner, job, or profile details are missing.",
      );
      return;
    }

    try {
      setIsSendingPartnerMessage(true);

      const obj = {
        partnerId,
        jobId,
        userId,
        message,
      };

      const response = await contactPartnerApi(obj);

      if (Number(response?.code) === 200) {
        setContactModalOpen(false);
        MessageManager.showToast("success", "Partner contacted");
        setPartnerMessage("");
      } else {
        MessageManager.showToast(
          "error",
          response?.message || "Unable to send message to partner.",
        );
      }
    } catch (error) {
      console.error("Error sending message to partner:", error);
      MessageManager.showToast(
        "error",
        error?.response?.data?.message ||
          error?.message ||
          "Unable to send message to partner.",
      );
    } finally {
      setIsSendingPartnerMessage(false);
    }
  };

  const handleToggleSavedProfile = () => {
    if (!selectedRole) {
      MessageManager.showToast(
        "warning",
        "Open this profile from a Hiring role to update its shortlist.",
      );
      return;
    }

    const result = toggleProfileInRoleShortlist(selectedRole, externalProfile);

    setShortlists(result.state);

    MessageManager.showToast(
      "success",
      result.added ? "Added to shortlist" : "Removed from shortlist",
    );
  };

  const viewExternalProfileApiHandler = useCallback(async () => {
    try {
      if (!externalProfileRequestUserId || !externalProfileRequestJobId) {
        return;
      }

      const obj = {
        userId: externalProfileRequestUserId,
        jobId: externalProfileRequestJobId,
      };

      const response = await viewExternalProfileApi(obj);

      if (Number(response?.code ?? response?.data?.code) === 200) {
        const apiProfile = extractExternalProfileData(response);

        if (apiProfile) {
          setExternalProfileApiData(apiProfile);
        }
      }
    } catch (error) {
      console.error("Error viewing external profile:", error);
    }
  }, [externalProfileRequestJobId, externalProfileRequestUserId]);

  useEffect(() => {
    setExternalProfileApiData(null);
  }, [externalProfileRequestJobId, externalProfileRequestUserId]);

  useEffect(() => {
    void viewExternalProfileApiHandler();
  }, [viewExternalProfileApiHandler]);

  const renderAssessmentStep = ({ icon: Icon, label, completed, detail }) => (
    <div
      className={`external-detail-status-step ${completed ? "completed" : ""}`}
    >
      <span className="external-detail-status-icon">
        <Icon size={16} />
      </span>
      <div>
        <strong>{label}</strong>
        <small>{detail}</small>
      </div>
    </div>
  );

  return (
    <div className="external-candidate-page">
      <BackButton />
      <main className="external-candidate-main">
        <div className="container">
          <section className="external-candidate-hero">
            <div className="external-candidate-avatar" aria-hidden="true">
              {canShowIdentity ? (
                getInitials(displayName)
              ) : (
                <ShieldCheck size={32} />
              )}
            </div>

            <div className="external-candidate-title-block">
              <span className="external-candidate-eyebrow">
                Partner-shared profile
              </span>
              <h1>{displayName}</h1>
              {canShowIdentity && sharedEmail && (
                <p className="external-candidate-email">{sharedEmail}</p>
              )}
              <p>{externalProfile.partner}</p>
              {!canShowIdentity && (
                <div className="external-candidate-privacy-note">
                  Email is hidden based on partner sharing settings.
                </div>
              )}
            </div>

            <div className="external-candidate-actions">
              {/* <button
                type="button"
                className={`external-candidate-action-btn ${isSaved ? "saved" : ""}`}
                onClick={handleToggleSavedProfile}
              >
                {isSaved
                  ? "Remove from Shortlist"
                  : "Add to Shortlist"}
              </button> */}
              <button
                type="button"
                className="external-candidate-action-btn primary"
                onClick={openContactPartner}
              >
                <Send size={15} />
                Contact Partner
              </button>
            </div>
          </section>

          <section className="external-candidate-status-card">
            <div className="external-candidate-status-title">
              <span>Profile Status</span>
              <strong>
                {externalProfile.profileStatus || "Profile shared by partner"}
              </strong>
            </div>
            <div className="external-detail-status-grid">
              {renderAssessmentStep({
                icon: Lightbulb,
                label: "Questions",
                completed: Boolean(assessment.quizCompleted),
                detail: `${externalProfile.completedQuestionsCount || 0} completed`,
              })}
              {renderAssessmentStep({
                icon: Award,
                label: "Skill Profile",
                completed: Boolean(assessment.skillAssessmentCompleted),
                detail: assessment.skillAssessmentCompleted
                  ? "Completed"
                  : "Pending",
              })}
              {renderAssessmentStep({
                icon: FileUp,
                label: "Resume",
                completed: Boolean(assessment.resumeUploadCompleted),
                detail: assessment.resumeUploadCompleted
                  ? "Uploaded"
                  : "Pending",
              })}
            </div>
          </section>

          <nav
            className="external-candidate-tabs"
            aria-label="External profile sections"
          >
            {tabs.map((tab) => (
              <button
                key={tab.id}
                type="button"
                className={`external-candidate-tab ${activeTab === tab.id ? "active" : ""}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </nav>

          <section className="external-candidate-tab-panel">
            {activeTab === "overview" && (
              <div className="external-candidate-overview-grid">
                <div className="profile-section-unique">
                  <h3>
                    <User size={20} />
                    Shared Identity
                  </h3>

                  <div className="education-info-unique">
                    <div className="education-item-unique">
                      <strong>Name</strong>
                      <span>{displayName}</span>
                    </div>

                    {canShowIdentity && (
                      <>
                        <div className="education-item-unique">
                          <strong>Email</strong>
                          <span>{displayEmail}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="profile-section-unique">
                  <h3>
                    <Briefcase size={20} />
                    Partner & Availability
                  </h3>

                  <div className="education-info-unique">
                    <div className="education-item-unique">
                      <strong>Partner</strong>
                      <span>
                        {externalProfile.partner || "Partner organization"}
                      </span>
                    </div>

                    <div className="education-item-unique">
                      <strong>Availability</strong>
                      <span>{externalProfile.availability || "Unknown"}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {activeTab === "skills" && (
              <div className="external-skills-content">
                {skillPerformanceItems.length > 0 && (
                  <article className="external-candidate-section-card">
                    <h3>Skills</h3>
                    <div className="external-skills-grid">
                      {skillPerformanceItems.map((skillItem) => {
                        const skillName =
                          skillItem.name || skillItem.skill || skillItem.label;
                        const skillScore = Number(
                          skillItem.score || skillItem.value || skillItem.level,
                        );
                        const hasScore =
                          Number.isFinite(skillScore) && skillScore > 0;
                        const formattedSkillScore = hasScore
                          ? skillScore.toFixed(1)
                          : "";

                        return (
                          <div key={skillName} className="external-skill-item">
                            <span className="external-skill-name">
                              {skillName}
                            </span>
                            {hasScore && (
                              <div className="external-skill-level">
                                <div className="external-skill-bar">
                                  <span
                                    style={{
                                      width: `${Math.min(skillScore, 100)}%`,
                                    }}
                                  />
                                </div>
                                <strong>{formattedSkillScore}%</strong>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </article>
                )}

                {performanceMetrics.length > 0 && (
                  <article className="external-candidate-section-card">
                    <h3>Performance</h3>
                    <div className="external-performance-grid">
                      {performanceMetrics.map((metric) => (
                        <div
                          key={metric.label || metric.name}
                          className="external-performance-item"
                        >
                          <span>{metric.label || metric.name}</span>
                          <strong>{metric.value || metric.score}</strong>
                          {metric.description && <p>{metric.description}</p>}
                        </div>
                      ))}
                    </div>
                  </article>
                )}

                {skillPerformanceItems.length === 0 &&
                  performanceMetrics.length === 0 && (
                    <article className="external-candidate-section-card">
                      <h3>Skills & Performance</h3>
                      <p className="external-empty-copy">
                        Skills and performance data are not available for this
                        shared profile yet.
                      </p>
                    </article>
                  )}
              </div>
            )}

            {activeTab === "matches" && (
              <article className="external-candidate-section-card">
                <h3>Top Role Matches</h3>
                <p className="external-section-helper">
                  Showing available matches without exposing the candidate's
                  current job role or department.
                </p>
                <div className="external-match-list">
                  {roleMatches.length > 0 ? (
                    roleMatches.map((match, index) => (
                      <div
                        key={`${match.title}-${index}`}
                        className="external-match-card"
                      >
                        <div className="external-match-rank">
                          {String(index + 1).padStart(2, "0")}
                        </div>
                        <div className="external-match-main">
                          <strong>{match.title}</strong>
                          <span>{match.department}</span>
                          <div className="external-match-meter">
                            <i style={{ width: `${match.match || 0}%` }} />
                          </div>
                        </div>
                        <strong className="external-match-score">
                          {match.match || 0}%
                        </strong>
                      </div>
                    ))
                  ) : (
                    <p className="external-empty-copy">
                      No role matches available.
                    </p>
                  )}
                </div>
              </article>
            )}
          </section>
        </div>
      </main>

      {contactModalOpen && (
        <div
          className="external-contact-overlay"
          role="dialog"
          aria-modal="true"
        >
          <div className="external-contact-modal">
            <button
              type="button"
              className="external-contact-close"
              aria-label="Close"
              onClick={() => setContactModalOpen(false)}
            >
              <X size={18} />
            </button>
            <h3>Contact Partner</h3>
            <p>
              Send a message about profile {displayName} to{" "}
              {externalProfile.partner}.
            </p>
            <textarea
              value={partnerMessage}
              onChange={(event) => setPartnerMessage(event.target.value)}
              rows={5}
            />
            <div className="external-contact-actions">
              <button type="button" onClick={() => setContactModalOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="primary"
                onClick={sendPartnerMessage}
                disabled={!partnerMessage.trim() || isSendingPartnerMessage}
              >
                <Mail size={15} />
                {isSendingPartnerMessage ? "Sending..." : "Send Message"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExternalCandidateProfilePage;
